import { MEMBER_COLORS, type MemberColor } from '@awwyeah/shared';
import { groupByColor, takenHint, type ColorHolder } from './takenColors';

export interface ColorPickerProps {
  value: MemberColor | null;
  onChange: (color: MemberColor) => void;
  /**
   * Other household members (or, pre-signup, invite-time colour hints) already using a
   * colour from this palette — excluding whoever is being edited. Taken colours stay fully
   * selectable ("family, not bureaucracy"); this just nudges toward free ones.
   */
  takenBy?: readonly { color: string; name: string; avatarEmoji?: string }[];
}

/** Swatch grid over the 16-colour member palette (`packages/shared` tokens). */
export function ColorPicker({ value, onChange, takenBy = [] }: ColorPickerProps): JSX.Element {
  const taken = groupByColor(takenBy);
  const selectedHolders: readonly ColorHolder[] = value ? (taken.get(value) ?? []) : [];

  return (
    <div>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Pick a colour">
        {MEMBER_COLORS.map((color) => {
          const selected = value === color;
          const holders = taken.get(color) ?? [];
          const isTaken = holders.length > 0;
          const label = isTaken ? `${color} — ${takenHint(holders)}` : color;
          return (
            <button
              key={color}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={label}
              title={isTaken ? takenHint(holders) : undefined}
              onClick={() => onChange(color)}
              className={`relative flex h-11 w-11 items-center justify-center rounded-full transition ease-app ${
                selected ? 'ring-2 ring-offset-2 ring-line-strong' : 'ring-1 ring-line'
              }`}
              style={{ backgroundColor: color }}
              data-testid="color-swatch"
              data-color={color}
              data-taken={isTaken || undefined}
            >
              {isTaken && (
                <span
                  className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-surface text-[9px] leading-none ring-1 ring-line"
                  aria-hidden="true"
                  data-testid="color-swatch-taken-badge"
                >
                  {holders[0]?.avatarEmoji ?? holders[0]?.name.charAt(0).toUpperCase()}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {selectedHolders.length > 0 && (
        <p className="mt-2 text-xs text-fg-3" data-testid="color-taken-hint">
          Also {takenHint(selectedHolders)} — still fine, just so you know.
        </p>
      )}
    </div>
  );
}
